import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CONTACT } from "@/lib/site-data";
import { JOURNEYS } from "@/lib/packages-data";
import useContent from "@/lib/useContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.76, 0, 0.24, 1];
const VIEWPORT = { once: true, margin: "-80px" };
const EMPTY_FORM = {
  name: "",
  email: "",
  phone: "",
  travel_date: "",
  nights: "",
  travelers: "",
  message: "",
};
const inputCls =
  "rounded-none border-0 border-b border-foreground/25 bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary h-11 text-base font-light";

export default function Contact({ selectedInterest, setSelectedInterest }) {
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-40 bg-background border-t border-foreground/10"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid md:grid-cols-12 gap-10 md:gap-20">
        <div className="md:col-span-5">
          <ContactInfo />
        </div>
        <div className="md:col-span-7">
          <LeadForm
            selectedInterest={selectedInterest}
            setSelectedInterest={setSelectedInterest}
          />
        </div>
      </div>
    </section>
  );
}

function ContactInfo() {
  return (
    <>
      <div className="text-[11px] uppercase tracking-[0.3em] text-foreground/60 mb-6">
        <div className="flex items-center gap-3">
          <span className="w-6 h-px bg-primary" />
          <span>Ch. 07 · Enquire</span>
        </div>
      </div>
      <motion.h2
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={VIEWPORT}
        transition={{ duration: 1, ease: EASE }}
        className="font-serif font-light text-5xl md:text-7xl leading-[0.94] tracking-tighter"
      >
        Let's begin a <em className="italic text-primary">correspondence.</em>
      </motion.h2>

      <p className="mt-6 text-base md:text-lg text-foreground/70 max-w-md leading-relaxed font-light">
        Share a rough idea of dates and interests. A named curator will
        answer within 24 hours with a considered first draft — no forms,
        no bots.
      </p>

      <div className="mt-12 space-y-8 border-t border-foreground/15 pt-8">
        <ContactRow label="Call" primary>
          <a
            href={`tel:${CONTACT.phone1.replace(/\s/g, "")}`}
            data-testid="contact-phone-1"
            className="hover:text-primary transition-colors"
          >
            {CONTACT.phone1}
          </a>
          <span className="text-foreground/40 mx-2">·</span>
          <a
            href={`tel:${CONTACT.phone2.replace(/\s/g, "")}`}
            data-testid="contact-phone-2"
            className="hover:text-primary transition-colors"
          >
            {CONTACT.phone2}
          </a>
        </ContactRow>
        <ContactRow label="Write">
          <a
            href={`mailto:${CONTACT.email}`}
            data-testid="contact-email"
            className="hover:text-primary transition-colors break-all"
          >
            {CONTACT.email}
          </a>
        </ContactRow>
        <ContactRow label="Visit">
          <span data-testid="contact-location">{CONTACT.location}</span>
        </ContactRow>
      </div>
    </>
  );
}

function LeadForm({ selectedInterest, setSelectedInterest }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please tell us your name and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        interest: selectedInterest || "General enquiry",
        source: "landing_page",
      });
      toast.success("Kadrinchey — your inquiry is on its way.");
      setDone(true);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          "Something went wrong. Please try again in a moment."
      );
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <LeadSuccess
        onReset={() => {
          setDone(false);
          setForm(EMPTY_FORM);
        }}
      />
    );
  }

  return (
    <motion.form
      onSubmit={submit}
      data-testid="lead-form"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={VIEWPORT}
      transition={{ duration: 1, ease: EASE }}
      className="border border-foreground/15 bg-background p-6 md:p-10"
    >
      <LeadFields
        form={form}
        update={update}
        selectedInterest={selectedInterest}
        setSelectedInterest={setSelectedInterest}
      />

      <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 border-t border-foreground/15 pt-8">
        <p className="text-xs text-foreground/50 max-w-sm leading-relaxed">
          By sending this, you agree we may reach out over email or
          phone about your journey. Zero spam. Ever.
        </p>
        <Button
          type="submit"
          disabled={loading}
          data-testid="lead-submit"
          className="btn-sharp inline-flex items-center gap-3 border border-foreground bg-transparent hover:bg-transparent text-foreground rounded-none px-7 py-6 text-[11px] uppercase tracking-[0.24em] font-medium disabled:opacity-60"
        >
          {loading ? "Sending…" : "Send Inquiry"}
          <span aria-hidden>→</span>
        </Button>
      </div>
    </motion.form>
  );
}

function LeadFields({ form, update, selectedInterest, setSelectedInterest }) {
  const journeys = useContent("journeys", JOURNEYS);
  const options = [...journeys.map((j) => j.title), "Airtickets only", "Custom itinerary"];
  return (
    <>
      <div className="grid md:grid-cols-2 gap-5 md:gap-6">
        <Field label="01 · Your name">
          <Input required value={form.name} onChange={update("name")} data-testid="lead-name" placeholder="Jigme Dorji" className={inputCls} />
        </Field>
        <Field label="02 · Email">
          <Input required type="email" value={form.email} onChange={update("email")} data-testid="lead-email" placeholder="you@example.com" className={inputCls} />
        </Field>
        <Field label="03 · Phone (optional)">
          <Input value={form.phone} onChange={update("phone")} data-testid="lead-phone" placeholder="+91 98xxx xxxxx" className={inputCls} />
        </Field>
        <Field label="04 · Travel dates">
          <Input value={form.travel_date} onChange={update("travel_date")} data-testid="lead-date" placeholder="Oct 2026 · flexible" className={inputCls} />
        </Field>
        <Field label="05 · Number of nights">
          <Input value={form.nights} onChange={update("nights")} data-testid="lead-nights" placeholder="e.g. 7 nights" className={inputCls} />
        </Field>
        <Field label="06 · Travellers">
          <Input value={form.travelers} onChange={update("travelers")} data-testid="lead-travelers" placeholder="2 adults" className={inputCls} />
        </Field>
        <Field label="07 · Interested in (optional)">
          <Select value={selectedInterest || ""} onValueChange={(v) => setSelectedInterest(v)}>
            <SelectTrigger
              data-testid="lead-interest"
              className="rounded-none border-0 border-b border-foreground/25 bg-transparent px-0 focus:ring-0 h-11 text-base font-light"
            >
              <SelectValue placeholder="Pick a journey (or leave blank)" />
            </SelectTrigger>
            <SelectContent>
              {options.map((o) => (
                <SelectItem key={o} value={o}>
                  {o}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </div>

      <div className="mt-8">
        <Field label="08 · Tell us more">
          <Textarea
            value={form.message}
            onChange={update("message")}
            data-testid="lead-message"
            placeholder="Pace, interests, must-sees, dietary needs — anything at all."
            rows={4}
            className="rounded-none border border-foreground/20 bg-transparent focus-visible:ring-0 focus-visible:border-primary text-base font-light resize-none"
          />
        </Field>
      </div>
    </>
  );
}

function LeadSuccess({ onReset }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, ease: EASE }}
      data-testid="lead-success"
      className="border border-primary/40 bg-primary/5 p-10 md:p-16 text-center"
    >
      <div className="text-[11px] uppercase tracking-[0.3em] text-primary mb-6">
        Kadrinchey · Thank you
      </div>
      <h3 className="font-serif text-4xl md:text-6xl font-light leading-[0.98] tracking-tight">
        Your inquiry is<br />
        <em className="italic text-primary">on its way.</em>
      </h3>
      <p className="mt-6 text-base md:text-lg text-foreground/70 max-w-md mx-auto font-light">
        A named curator from Paro will reply within 24 hours. Check
        your inbox for a confirmation from us.
      </p>
      <button
        onClick={onReset}
        data-testid="lead-reset"
        className="mt-10 inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.24em] font-medium border-b border-foreground/60 pb-1 hover:text-primary hover:border-primary transition-colors"
      >
        Send another
      </button>
    </motion.div>
  );
}

function ContactRow({ label, primary, children }) {
  return (
    <div className="grid grid-cols-[80px_1fr] gap-6 items-baseline">
      <div className="text-[10px] uppercase tracking-[0.28em] text-foreground/55">
        {label}
      </div>
      <div
        className={`${
          primary ? "font-serif text-2xl md:text-3xl" : "font-serif text-xl md:text-2xl"
        } font-light`}
      >
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.28em] text-foreground/55 block mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}
